import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useState } from 'react'

import { BottomSheet, Popover } from '@flowwai-frontend/design-system'
import {
   ClockFastBackwardIcon,
   DotsHorizontal,
   Maximize01Icon,
   Minimize01Icon,
   PlusIcon,
   XCloseIcon
} from '@flowwai-frontend/design-system/icons'
import {
   ChatConfigUIStateEnum,
   ChatHistoryOpenedFromInfo,
   useAIChatUIStore
} from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import {
   ChatHistoryOptionType,
   MenuOptionType,
   MenuOptionEnum
} from '@flowwai-frontend/shared-web/ai-chat-support/ui'
import { isMobile } from '@flowwai-frontend/shared/utils/web'

import AIChatSupportController from '../AIChatSupportController/AIChatSupportController'

import * as Styles from './styles'
import './index.css'

interface AIChatBoxControllerProps {
   className?: string
   onClose: () => void
}

const AIChatBoxController = (
   props: AIChatBoxControllerProps
): React.ReactElement => {
   const { className, onClose } = props
   const [isChatBoxExpanded, setIsChatBoxExpanded] = useState<boolean | null>(
      null
   ) //NOTE: maintaining null in order to apply chat-box-open-keyframe on initial mount
   const [
      shouldShowChatHistoryInSidepeek,
      setShouldShowChatHistoryInSidepeek
   ] = useState(false)

   const { theme } = useAppTheme()
   const themes = theme.aiChatSupport.aiActionCenter
   const isMobileDevice = isMobile()
   const t = useCustomT('aiChatSupport:menuOptions')
   const aiChatUIStore = useAIChatUIStore()
   const [isMenuOptionsPopoverOpen, setIsMenuOptionsPopoverOpen] = useState(
      false
   )

   const onClickExpand = () => {
      setIsChatBoxExpanded(!isChatBoxExpanded)

      setShouldShowChatHistoryInSidepeek(false)
   }

   const closeMenuOptionsPopover = () => {
      setIsMenuOptionsPopoverOpen(false)
   }

   const renderExpandIcon = () => (
      <button onClick={onClickExpand}>
         {isChatBoxExpanded ? (
            <Minimize01Icon
               width={20}
               height={20}
               fill={themes.expandOrCollapseIconColor}
            />
         ) : (
            <Maximize01Icon
               width={20}
               height={20}
               fill={themes.expandOrCollapseIconColor}
            />
         )}
      </button>
   )

   const renderMenuOptionTrigger = (): React.ReactElement => (
      <div
         className='cursor-pointer'
         onClick={() => {
            setIsMenuOptionsPopoverOpen(true)
         }}
      >
         <DotsHorizontal width={24} height={24} stroke={themes.menuIconColor} />
      </div>
   )

   const renderMenuOptionsList = (
      options: MenuOptionType[]
   ): React.ReactElement => (
      <div className={Styles.menuOptionsListContainer}>
         {options.map(option => (
            <div
               key={option.id}
               className={cn(
                  Styles.menuOptionContainer,
                  themes.menuOptionHoverColor
               )}
               onClick={option.onClick}
            >
               <div className='shrink-0'>{option.icon()}</div>
               <span
                  className={cn(
                     Styles.menuOptionText,
                     themes.menuOptionTextColor
                  )}
                  title={option.name}
               >
                  {option.name}
               </span>
            </div>
         ))}
      </div>
   )

   const renderMenuOptions = (options: MenuOptionType[]): React.ReactElement =>
      isMobile() ? (
         <>
            {renderMenuOptionTrigger()}
            <BottomSheet
               isOpen={isMenuOptionsPopoverOpen}
               onRequestClose={() => {
                  setIsMenuOptionsPopoverOpen(false)
               }}
               onAfterClose={() => {
                  setIsMenuOptionsPopoverOpen(false)
               }}
            >
               {renderMenuOptionsList(options)}
            </BottomSheet>
         </>
      ) : (
         <Popover
            popoverBodyContainerClassName={cn('w-[320px] !max-h-[370px]')}
            isOpen={isMenuOptionsPopoverOpen}
            renderPopoverBody={() => renderMenuOptionsList(options)}
            renderPopoverTrigger={renderMenuOptionTrigger}
            onOpenChange={setIsMenuOptionsPopoverOpen}
            placement='bottom right'
         />
      )

   const getHeaderMenuOptionsList = (
      isChatHistoryAvailable: boolean,
      chatHistoryOpenedFromInfo: ChatHistoryOpenedFromInfo,
      onClickNewThread?: () => void
   ): MenuOptionType[] => {
      const options: MenuOptionType[] = []

      const shouldShowCreateNewThreadOption =
         aiChatUIStore.activeUIStateEnum !== ChatConfigUIStateEnum.SearchState

      if (shouldShowCreateNewThreadOption) {
         options.push({
            id: MenuOptionEnum.NewChat,
            name: t('newChat'),
            onClick: () => {
               onClickNewThread?.()
               aiChatUIStore.setActiveUIStateEnum(
                  ChatConfigUIStateEnum.SearchState
               )
               closeMenuOptionsPopover()
            },
            icon: () => (
               <PlusIcon
                  height={18}
                  width={18}
                  fill={themes.menuOptionIconColor}
               />
            )
         })
      }
      if (isChatHistoryAvailable) {
         options.push({
            id: MenuOptionEnum.ChatHistory,
            name: t('chatHistory'),
            onClick: () => {
               aiChatUIStore.setActiveUIStateEnum(
                  ChatConfigUIStateEnum.ChatHistory
               )
               aiChatUIStore.setChatHistoryOpenedFromInfo(
                  chatHistoryOpenedFromInfo
               )
               closeMenuOptionsPopover()
            },
            icon: () => (
               <ClockFastBackwardIcon
                  height={18}
                  width={18}
                  fill={themes.menuOptionIconColor}
               />
            )
         })
      }

      return options
   }

   const renderHeader = (
      chatHistoryOptions: ChatHistoryOptionType[],
      chatHistoryOpenedFromInfo: ChatHistoryOpenedFromInfo,
      onClickNewThread?: () => void
   ): React.ReactElement => {
      const isChatHistoryAvailable = chatHistoryOptions.length > 0

      const options = getHeaderMenuOptionsList(
         isChatHistoryAvailable,
         chatHistoryOpenedFromInfo,
         onClickNewThread
      )
      const isOptionsAvailable = options.length > 0
      const shouldShowOptions = !isChatBoxExpanded && isOptionsAvailable

      return (
         <div className='flex flex-row items-center justify-between'>
            <div className='flex items-center gap-x-[20px] self-end gap-2 ml-auto'>
               {shouldShowOptions ? renderMenuOptions(options) : null}
               {!isMobileDevice ? renderExpandIcon() : null}
               <button onClick={onClose}>
                  <XCloseIcon fill={themes.chatBoxCloseIconFillColor} />
               </button>
            </div>
         </div>
      )
   }

   return (
      <div
         className={cn(
            Styles.chatBoxContainerStyles,
            isMobileDevice
               ? Styles.mobileChatBoxContainerStyles
               : Styles.getDesktopChatBoxContainerStyles(isChatBoxExpanded),
            themes.cardBgColor,
            themes.defaultBorderColor
         )}
         onClick={e => e.stopPropagation()}
      >
         <AIChatSupportController
            renderActionButtons={renderHeader}
            onClose={onClose}
            isChatBoxExpanded={Boolean(isChatBoxExpanded)}
            shouldShowChatHistoryInSidepeek={shouldShowChatHistoryInSidepeek}
            setShouldShowChatHistoryInSidepeek={
               setShouldShowChatHistoryInSidepeek
            }
         />
      </div>
   )
}

export default observer(AIChatBoxController)
